import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, DatePicker, Row, Spin } from 'antd';
import moment from 'moment';
import cx from 'classnames';
import { isNil } from 'lodash';

import {
  getReportsDatesRangeInfo,
  setNewEndDateForReports,
} from 'redux/reducers/homeDashboardSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ReportsDatesRangeInfoModel } from 'api/backofficeApiClient';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { convertMomentToDate, getDateWithoutTime } from 'utils/dateUtils';
import { hasAnyReportDetailsSelector } from 'redux/selectors/homeDashboardSelectors';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './ReportDateUpdater.module.less';

export default function ReportDateUpdater() {
  const [initialLoad, setInitialLoad] = useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(
    null,
  );
  const [reportDateRange, setReportDateRange] =
    useState<ReportsDatesRangeInfoModel>();
  const hasAnyReportDetails = useAppSelector(hasAnyReportDetailsSelector);
  const noAccessToReportDateUpdater = initialLoad && isNil(reportDateRange);

  const dispatch = useAppDispatch();

  const fetchReportDateRange = useCallback(async () => {
    const result = await dispatch(getReportsDatesRangeInfo());

    if (getReportsDatesRangeInfo.fulfilled.match(result)) {
      setReportDateRange(result.payload);
    }
    setInitialLoad(true);
  }, [dispatch]);

  useEffect(() => {
    fetchReportDateRange();
  }, [fetchReportDateRange]);

  const handleEndDateChange = (date: moment.Moment | null) => {
    setSelectedEndDate(date);
  };

  const handleUpdateReportEndDate = async () => {
    if (!selectedEndDate) return;

    const endDate = convertMomentToDate(selectedEndDate);

    const result = await dispatch(
      setNewEndDateForReports(getDateWithoutTime(endDate)),
    );

    if (setNewEndDateForReports.fulfilled.match(result)) {
      BaseAlert.success('The date was successfully changed.');
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const disabledDate = (current: moment.Moment) => {
    const startDate = moment(reportDateRange?.newDataPeriodDateFrom).startOf(
      'day',
    );
    const currentWeekSunday = moment().startOf('week').add(7, 'days');

    const isSunday = current.day() === 0;

    const isCurrentWeekSunday = current.isSame(currentWeekSunday, 'day');

    return !(
      isCurrentWeekSunday ||
      (isSunday &&
        current.isSameOrAfter(startDate) &&
        current.isBefore(moment()))
    );
  };

  if (noAccessToReportDateUpdater) return null;

  return (
    <Row className={styles.wrapperReportDateUpdater}>
      <Col xs={24} md={6} className={styles.wrapperDatePicker}>
        {initialLoad ? (
          <>
            <DatePicker
              value={selectedEndDate}
              placeholder="Select date"
              showToday={false}
              format="MM/DD/YYYY"
              className={cx('d-flex', styles.datePicker)}
              onChange={handleEndDateChange}
              disabledDate={disabledDate}
            />
            <Button
              onClick={handleUpdateReportEndDate}
              disabled={!selectedEndDate || !hasAnyReportDetails}
            >
              <span className="icon icon-download" />
            </Button>
          </>
        ) : (
          <Spin />
        )}
      </Col>
    </Row>
  );
}
